import React, { useRef, useState } from 'react';
import { Box, Button, Popover, Switch, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

/**
 * 테이블 컬럼 설정 팝오버(범용). useTableColumns 훅의 결과를 그대로 받는다.
 * - "왼쪽 고정됨" 그룹 / 일반 그룹. 행을 잡아 끌면 순서가 바뀌고, 다른 그룹에 떨어뜨리면 고정/해제된다.
 * - 스위치로 표시/숨김, 초기화로 화면 기본값 복원.
 * 드래그는 HTML5 DnD 만 쓴다(라이브러리 없음).
 */
/** @param hint 제목 옆 ⓘ 툴팁(선택). 예: 숨긴 컬럼은 엑셀에도 빠진다 */
const TableColumnSettings = ({ columns, onToggle, onMove, onSetPinned, onReset, title = '컬럼 설정', hint }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const dragKey = useRef(null);
  const lastDrop = useRef(null); // 같은 자리에서 dragover 가 반복될 때 이동 호출을 막는다
  // 드래그 중 마우스가 올라가 있는 그룹(true=고정, false=일반). 그 그룹 영역에 테두리 포커스를 준다
  const [dragOverPinned, setDragOverPinned] = useState(null);

  const pinned = columns.filter(c => c.pinned);
  const normal = columns.filter(c => !c.pinned);

  const handleDragStart = (e, key) => {
    dragKey.current = key;
    lastDrop.current = null;
    e.dataTransfer.effectAllowed = 'move';
    // Firefox 는 setData 가 없으면 드래그가 시작되지 않는다
    e.dataTransfer.setData('text/plain', key);
  };

  const handleRowDragOver = (e, key, groupPinned) => {
    e.preventDefault();
    setDragOverPinned(groupPinned);
    const from = dragKey.current;
    if (!from || from === key) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const after = e.clientY > rect.top + rect.height / 2;
    const sig = `${key}:${after}`;
    if (lastDrop.current === sig) return;
    lastDrop.current = sig;
    onMove(from, key, after);
  };

  // 빈 그룹에 떨어뜨릴 때만 여기로 온다(행 위에서는 행 핸들러가 먼저 잡는다)
  const handleGroupDragOver = (e, groupPinned, isEmpty) => {
    e.preventDefault();
    setDragOverPinned(groupPinned);
    if (!isEmpty || !dragKey.current) return;
    onSetPinned(dragKey.current, groupPinned);
  };

  const handleDragEnd = () => {
    dragKey.current = null;
    lastDrop.current = null;
    setDragOverPinned(null);
  };

  const renderRow = column => (
    <Row
      key={column.key}
      draggable
      onDragStart={e => handleDragStart(e, column.key)}
      onDragOver={e => handleRowDragOver(e, column.key, column.pinned)}
      onDrop={e => e.preventDefault()}
      onDragEnd={handleDragEnd}>
      <DragIndicatorIcon sx={{ fontSize: 18, color: '#bbb', cursor: 'grab', flexShrink: 0 }} />
      <RowLabel>{column.label}</RowLabel>
      {column.hint && (
        <Tooltip title={column.hint} arrow placement="left">
          <InfoOutlinedIcon sx={{ fontSize: 15, color: '#9aa0a6', flexShrink: 0 }} />
        </Tooltip>
      )}
      <Switch size="small" checked={column.visible} onChange={() => onToggle(column.key)} sx={switchSx} />
    </Row>
  );

  return (
    <>
      <OpenButton onClick={e => setAnchorEl(e.currentTarget)} variant="outlined">
        <ViewColumnOutlinedIcon sx={{ fontSize: 17 }} />
        {title}
      </OpenButton>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 280, maxHeight: 520, display: 'flex', flexDirection: 'column', borderRadius: '10px' } }}>
        <Header>
          <HeaderRow>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#222' }}>{title}</Typography>
              {hint && (
                <Tooltip title={hint} arrow placement="bottom-start">
                  <InfoOutlinedIcon sx={{ fontSize: 15, color: '#9aa0a6', cursor: 'help' }} />
                </Tooltip>
              )}
            </Box>
            <ResetButton onClick={onReset}>
              <RestartAltIcon sx={{ fontSize: 15 }} />
              초기화
            </ResetButton>
          </HeaderRow>
        </Header>
        <Body>
          <GroupTitle>
            <PushPinOutlinedIcon sx={{ fontSize: 14 }} />
            왼쪽 고정됨
          </GroupTitle>
          <Group
            onDragOver={e => handleGroupDragOver(e, true, pinned.length === 0)}
            onDragLeave={() => setDragOverPinned(null)}
            onDrop={e => e.preventDefault()}
            empty={pinned.length === 0}
            active={dragOverPinned === true}>
            {pinned.length > 0 ? pinned.map(renderRow) : <EmptyHint>여기로 끌어오면 왼쪽에 고정됩니다</EmptyHint>}
          </Group>
          <Divider />
          <Group
            onDragOver={e => handleGroupDragOver(e, false, normal.length === 0)}
            onDragLeave={() => setDragOverPinned(null)}
            onDrop={e => e.preventDefault()}
            empty={normal.length === 0}
            active={dragOverPinned === false}>
            {normal.length > 0 ? normal.map(renderRow) : <EmptyHint>여기로 끌어오면 고정이 풀립니다</EmptyHint>}
          </Group>
        </Body>
      </Popover>
    </>
  );
};

const switchSx = {
  ml: 'auto',
  '& .MuiSwitch-switchBase.Mui-checked': { color: '#0F7F85' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#0F7F85' },
};

// 아이콘만 있으면 뭔지 알기 어렵다는 피드백으로 라벨을 붙였다. 옆의 그룹 관리·SMS 버튼과 같은 모양
const OpenButton = styled(Button)({
  minWidth: '90px',
  height: '34px',
  padding: '6px 14px',
  borderRadius: '6px',
  border: '1px solid #4AB3BC',
  background: '#fff',
  color: '#4AB3BC',
  fontSize: '13px',
  fontWeight: 500,
  boxShadow: 'none',
  gap: '6px',
  whiteSpace: 'nowrap',
  '&:hover': { background: 'rgba(74, 179, 188, 0.06)', border: '1px solid #4AB3BC', boxShadow: 'none' },
});

const Header = styled(Box)({
  padding: '12px 14px 8px',
  borderBottom: '1px solid #eee',
});

const HeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});



const ResetButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
  border: 'none',
  background: 'none',
  color: '#888',
  fontSize: 12,
  cursor: 'pointer',
  padding: '2px 4px',
  '&:hover': { color: '#0F7F85' },
});

const Body = styled(Box)({
  overflowY: 'auto',
  padding: '6px 6px 10px',
});

const GroupTitle = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  fontSize: 12,
  color: '#888',
  padding: '6px 8px 2px',
});

// 드래그 중 마우스가 올라간 그룹은 테두리·배경으로 "여기에 떨어진다"를 보여준다
const Group = styled(Box, { shouldForwardProp: p => p !== 'empty' && p !== 'active' })(({ empty, active }) => ({
  minHeight: 36,
  borderRadius: 6,
  border: '1px dashed transparent',
  transition: 'border-color 0.12s, background-color 0.12s',
  ...(empty && { borderColor: '#ddd', margin: '4px 6px' }),
  ...(active && { borderColor: '#0F7F85', backgroundColor: '#f2f9f9' }),
}));

const EmptyHint = styled(Box)({
  fontSize: 11,
  color: '#aaa',
  textAlign: 'center',
  padding: '10px 0',
});

const Divider = styled(Box)({
  height: 1,
  background: '#eee',
  margin: '6px 8px',
});

const Row = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 8px',
  borderRadius: 6,
  '&:hover': { backgroundColor: '#f5f7f8' },
});

const RowLabel = styled('span')({
  fontSize: 13,
  color: '#333',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export default TableColumnSettings;
